import React, { useState, useEffect } from "react";
import { Modal, Input, Button, List, message } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";

function getTypeLabel(type) {
  const t = parseInt(type, 10);
  if (t === 1) return "Program";
  if (t === 2) return "Registration";
  if (t === 4) return "Team";
  return "source";
}

function normalizeMembers(emails) {
  if (!Array.isArray(emails)) return [];
  return emails
    .map((m) =>
      typeof m === "object" && m !== null
        ? m.email || m.phone || m.value || ""
        : String(m ?? "")
    )
    .filter(Boolean);
}

function AudienceMembersModal({ visible, audience, onCancel, onSave, isSaving }) {
  const isSms = (audience?.channel || "email") === "sms";
  const isManual = parseInt(audience?.type, 10) === 3;
  const label = isSms ? "phone number" : "email address";

  const [members, setMembers] = useState([]);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (visible && audience) {
      setMembers(normalizeMembers(audience.emails));
    }
    setInputValue("");
  }, [visible, audience]);

  const validate = (val) => {
    if (isSms) return /^\+?[\d\s\-().]{7,15}$/.test(val);
    return /^\S+@\S+\.\S+$/.test(val);
  };

  const handleAdd = () => {
    const val = inputValue.trim();
    if (!val) return;
    if (!validate(val)) {
      message.error(`Invalid ${label}`);
      return;
    }
    const normalized = isSms ? val.replace(/[^+\d]/g, "") : val.toLowerCase();
    if (members.includes(normalized)) {
      message.warning(`That ${label} is already in the list`);
      return;
    }
    setMembers((prev) => [...prev, normalized]);
    setInputValue("");
  };

  const handleRemove = (member) => {
    setMembers((prev) => prev.filter((m) => m !== member));
  };

  const handleSave = () => {
    if (members.length === 0) {
      message.error("At least one member is required");
      return;
    }
    onSave(members);
  };

  const footer = [
    <Button key="cancel" onClick={onCancel}>Cancel</Button>,
    <Button key="save" type="primary" loading={isSaving} onClick={handleSave}>
      Save
    </Button>,
  ];

  return (
    <Modal
      title={`Members — ${audience?.name || ""}`}
      open={visible}
      onCancel={onCancel}
      width={520}
      footer={footer}
      destroyOnClose
    >
      {!isManual && (
        <div
          style={{
            marginBottom: 12,
            padding: "7px 12px",
            background: "#fffbe6",
            border: "1px solid #ffe58f",
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          Members are synced from the linked {getTypeLabel(audience?.type)}.
          You can add extras below; to remove members update the source.
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Input
          placeholder={`Add ${label}…`}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onPressEnter={handleAdd}
          style={{ flex: 1 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add
        </Button>
      </div>

      <List
        size="small"
        dataSource={members}
        locale={{ emptyText: "No members" }}
        style={{
          maxHeight: 380,
          overflowY: "auto",
          border: "1px solid #f0f0f0",
          borderRadius: 4,
        }}
        renderItem={(member) => (
          <List.Item
            actions={
              isManual
                ? [
                    <CloseOutlined
                      style={{ color: "red", cursor: "pointer", fontSize: 12 }}
                      onClick={() => handleRemove(member)}
                    />,
                  ]
                : []
            }
          >
            <span style={{ wordBreak: "break-all" }}>{member}</span>
          </List.Item>
        )}
      />

      <div style={{ marginTop: 8, color: "#999", fontSize: 12 }}>
        {members.length} member{members.length !== 1 ? "s" : ""}
      </div>
    </Modal>
  );
}

export default AudienceMembersModal;
